/**
 * VeevaQueryBuilder — cascading-dropdown VQL builder (like the "Veeva VQL"
 * Chrome extension). Reads the connected vault's live metadata:
 *  - objects   → GET  /integration/veeva/:id/objects
 *  - fields    → POST /integration/veeva/:id/describe-object
 * and assembles a SELECT ... FROM ... [WHERE ...] [MAXROWS n] statement.
 *
 * It does not run the query itself — it writes the assembled VQL up to the
 * parent (VeevaBrowser) via `onVqlChange`, so the existing editable textarea
 * stays the single source of truth and power users can tweak before running.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  veevaDescribeObject,
  veevaListObjects,
  type FieldDefinition,
  type ObjectSummary,
} from "@/shared/api/integration-browse";

// VQL comparison operators the builder offers. LIKE always quotes its value.
// "starts with" → LIKE 'x%'. "contains" → FIND ('x*' SCOPE field): VQL SELECTs
// reject a leading wildcard (LIKE '%x%' is an error), so a contains search
// goes through Vault's search, which matches words beginning with the term.
const OPERATORS = ["=", "!=", "<", ">", "<=", ">=", "starts with", "contains", "LIKE"] as const;
type Operator = (typeof OPERATORS)[number];

interface Condition {
  field: string;
  op: Operator;
  value: string;
}

interface Props {
  connectionId: string;
  onVqlChange: (vql: string) => void;
  /** The object the builder is querying. Reported separately from the VQL so a
   *  downstream node can know it without parsing SQL — the pipeline's
   *  Save-to-Blob target needs it to reach a record's attachments and audit
   *  trail, and re-deriving it from text is brittle. */
  onObjectChange?: (object: string) => void;
}

const quote = (v: string) => `'${v.replace(/'/g, "''")}'`;

/** Quote a value unless it is a plain number or the literal null. */
function formatValue(op: Operator, raw: string): string {
  const v = raw.trim();
  if (op === "LIKE") {
    // Without a wildcard LIKE is an exact match ("LIKE 'Doc'" = "= 'Doc'"),
    // which is never what was meant; default to "starts with".
    return quote(v.includes("%") ? v : `${v}%`);
  }
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;
  if (v.toLowerCase() === "null") return "NULL";
  // Escape single quotes by doubling them (VQL string literal rule).
  return quote(v);
}

function whereClause(c: Condition): string {
  if (c.op === "starts with")
    return `${c.field} LIKE ${quote(`${c.value.trim().replace(/%/g, "")}%`)}`;
  return `${c.field} ${c.op} ${formatValue(c.op, c.value)}`;
}

function buildVql(
  object: string,
  fields: string[],
  conditions: Condition[],
  maxRows: number | "",
): string {
  if (!object) return "";
  const select = fields.length > 0 ? fields.join(", ") : "id";
  let vql = `SELECT ${select} FROM ${object}`;
  const active = conditions.filter((c) => c.field && c.value.trim() !== "");
  // VQL allows one FIND per query, so only the first "contains" is used.
  const contains = active.find((c) => c.op === "contains");
  if (contains) {
    const term = contains.value.trim().replace(/[*']/g, "");
    vql += ` FIND (${quote(`${term}*`)} SCOPE ${contains.field})`;
  }
  const clauses = active.filter((c) => c.op !== "contains").map(whereClause);
  if (clauses.length > 0) vql += ` WHERE ${clauses.join(" AND ")}`;
  if (typeof maxRows === "number" && maxRows > 0) vql += ` MAXROWS ${maxRows}`;
  return vql;
}

export function VeevaQueryBuilder({ connectionId, onVqlChange, onObjectChange }: Props) {
  const getToken = useAccessToken();

  const [objects, setObjects] = useState<ObjectSummary[]>([]);
  // No default object — the user picks one (avoids silently defaulting to
  // `documents` and avoids emitting a canned query into a pipeline node).
  const [object, setObject] = useState<string>("");
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [maxRows, setMaxRows] = useState<number | "">(50);

  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the object list once.
  useEffect(() => {
    let cancelled = false;
    setLoadingObjects(true);
    setError(null);
    void (async () => {
      try {
        const token = await getToken();
        const result = await veevaListObjects(token, connectionId);
        if (!cancelled) setObjects(result.items);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingObjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  // Load fields whenever the object changes; reset field/where selections.
  useEffect(() => {
    if (!object) return;
    let cancelled = false;
    setLoadingFields(true);
    setError(null);
    setSelectedFields([]);
    setConditions([]);
    void (async () => {
      try {
        const token = await getToken();
        const result = await veevaDescribeObject(token, connectionId, object);
        if (!cancelled) setFields(result.fields);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, object]);

  // Push the assembled VQL up when the BUILDER's inputs change — and only
  // then. The callbacks are read through refs: the parent passes new arrow
  // functions on every render, and depending on them re-emitted the built
  // query after every keystroke in the hand-edited VQL box, overwriting it.
  const onVqlChangeRef = useRef(onVqlChange);
  onVqlChangeRef.current = onVqlChange;
  const onObjectChangeRef = useRef(onObjectChange);
  onObjectChangeRef.current = onObjectChange;
  useEffect(() => {
    onVqlChangeRef.current(buildVql(object, selectedFields, conditions, maxRows));
    onObjectChangeRef.current?.(object);
  }, [object, selectedFields, conditions, maxRows]);

  const fieldOptions = fields.map((f) => ({
    value: f.name,
    label: f.label ? `${f.label} (${f.name})` : f.name,
  }));

  const addCondition = useCallback(() => {
    setConditions((prev) => [...prev, { field: "", op: "=", value: "" }]);
  }, []);

  const updateCondition = (index: number, patch: Partial<Condition>) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const removeCondition = (index: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Stack gap="sm">
      {error && (
        <Alert variant="light" color="red" title="Could not read vault metadata">
          {error}
        </Alert>
      )}

      <Group grow align="flex-end">
        <Select
          label="Object (FROM)"
          placeholder={loadingObjects ? "Loading…" : "Pick an object"}
          data={objects.map((o) => ({
            value: o.name,
            label: o.label ? `${o.label} (${o.name})` : o.name,
          }))}
          value={object}
          onChange={(v) => setObject(v ?? "documents")}
          searchable
          disabled={loadingObjects}
          allowDeselect={false}
          rightSection={loadingObjects ? <Loader size="xs" /> : undefined}
        />
        <NumberInput
          label="MAXROWS"
          value={maxRows}
          onChange={(v) => setMaxRows(typeof v === "number" ? v : "")}
          min={1}
          max={1000}
          w={140}
        />
      </Group>

      <MultiSelect
        label="Fields (SELECT)"
        description="Leave empty to select id only."
        placeholder={loadingFields ? "Loading fields…" : "Pick fields"}
        data={fieldOptions}
        value={selectedFields}
        onChange={setSelectedFields}
        searchable
        clearable
        disabled={loadingFields}
        rightSection={loadingFields ? <Loader size="xs" /> : undefined}
      />

      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={500}>
            Filters (WHERE)
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={addCondition}
            disabled={loadingFields}
          >
            Add filter
          </Button>
        </Group>

        {conditions.length === 0 && (
          <Text size="xs" c="dimmed">
            No filters — the query returns all rows (up to MAXROWS).
          </Text>
        )}

        {conditions.map((c, i) => (
          <Group key={i} gap="xs" align="flex-end" wrap="nowrap">
            <Select
              label={i === 0 ? "Field" : undefined}
              placeholder="Field"
              data={fieldOptions}
              value={c.field || null}
              onChange={(v) => updateCondition(i, { field: v ?? "" })}
              searchable
              style={{ flex: 1 }}
            />
            <Select
              label={i === 0 ? "Op" : undefined}
              data={OPERATORS as unknown as string[]}
              value={c.op}
              onChange={(v) => updateCondition(i, { op: (v as Operator) ?? "=" })}
              allowDeselect={false}
              w={90}
            />
            <TextInput
              label={i === 0 ? "Value" : undefined}
              placeholder="value"
              value={c.value}
              onChange={(e) => updateCondition(i, { value: e.currentTarget.value })}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => removeCondition(i)}
              aria-label="Remove filter"
              mb={4}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}
      </Stack>
    </Stack>
  );
}
